import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Flag, PauseCircle, CheckCircle2, RotateCcw, Mail, Phone } from "lucide-react";
import api, { inr, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const DISPUTE_LABELS = { not_delivered: "Paid but deal not delivered", payment_not_received: "Work done but not paid", fake_listing: "Fake / misrepresented listing", overcharged: "Overcharged / hidden fees", misbehaviour: "Misbehaviour / threats", other: "Other" };
const OUTCOMES = [["resolved_release", "Resolved — release payment to owner"], ["resolved_refund", "Resolved — refund tenant"], ["rejected", "Closed — no violation found"], ["resolved_other", "Resolved — other"]];

export default function DisputesPanel({ onHoldDone }) {
  const [status, setStatus] = useState("open");
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [outcome, setOutcome] = useState("resolved_other");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/disputes?status=${status}`).then(({ data }) => setData(data)).catch(() => toast.error("Could not load disputes"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);

  const holdPayout = async (d) => {
    try { await api.post(`/admin/disputes/${d.id}/hold-payout`); toast.success("Payout put on HOLD — see Payouts → On hold"); load(); onHoldDone?.(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const resolve = async () => {
    setBusy(true);
    try { await api.post(`/admin/disputes/${sel.id}/resolve`, { outcome, note }); toast.success("Dispute resolved — reporter emailed"); setSel(null); setNote(""); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const reopen = async (id) => {
    try { await api.post(`/admin/disputes/${id}/reopen`); toast.success("Dispute reopened"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <section className="mt-14" id="admin-disputes" data-testid="admin-disputes">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Disputes / cheating reports</div>
          <h2 className="font-display text-3xl mt-1">Reports from tenants &amp; owners</h2>
          <p className="text-sm text-[#5C6B7A] mt-1">Each report is also emailed to official@locanest.in. Hold the attached payout with one tap while you investigate.</p>
        </div>
        {data && <div className="text-sm text-[#5C6B7A]" data-testid="disputes-counts"><span className="text-[#C85A32] font-semibold">{data.counts.open}</span> open · {data.counts.resolved} resolved</div>}
      </div>
      <div className="mt-4 inline-flex rounded-md bg-[#F0EBE1] p-1 text-sm">
        {["open", "resolved", "all"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`px-4 py-1.5 rounded capitalize ${status === s ? "bg-[#FDFBF7] shadow-sm font-semibold" : "text-[#5C6B7A]"}`} data-testid={`disputes-filter-${s}`}>{s}</button>
        ))}
      </div>
      {!data ? <div className="mt-4 text-sm text-[#5C6B7A]">Loading…</div> : data.items.length === 0 ? (
        <div className="mt-4 bg-[#F0EBE1] rounded-lg p-8 text-center text-[#5C6B7A]" data-testid="disputes-empty">No {status === "all" ? "" : status} disputes.</div>
      ) : (
        <div className="mt-4 space-y-3" data-testid="disputes-list">
          {data.items.map((d) => (
            <div key={d.id} className={`rounded-lg border bg-[#F0EBE1] p-4 flex gap-4 flex-wrap ${d.status === "open" ? "border-red-300" : "border-[#1A1F2C]/10"}`} data-testid={`dispute-${d.id}`}>
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.16em] ${d.status === "resolved" ? "bg-[#D4DEC6] text-[#2B3A67]" : "bg-red-600 text-white"}`} data-testid={`dispute-status-${d.id}`}>{d.status}</span>
                  <span className="text-xs font-mono text-[#5C6B7A]">#{d.id.slice(0, 8)}</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-[#FDFBF7] border border-[#1A1F2C]/10"><Flag size={10} className="inline mr-1" />{DISPUTE_LABELS[d.category] || d.category}</span>
                  <span className="text-xs text-[#5C6B7A]">{new Date(d.created_at).toLocaleString()}</span>
                </div>
                <div className="mt-2 text-sm">
                  <b>{d.reporter_name}</b> <span className="text-xs text-[#5C6B7A]">({d.reporter_role})</span> reported <b>{d.against_name}</b> on deal <Link to={`/bookings/${d.booking_id}`} className="underline hover:text-[#C85A32]" data-testid={`dispute-booking-${d.id}`}>{d.agreement_number}</Link> · {d.property_title}
                </div>
                <div className="text-xs text-[#5C6B7A] flex items-center gap-3 flex-wrap mt-0.5">
                  <a href={`mailto:${d.reporter_email}?subject=Re: LocaNest report #${d.id.slice(0, 8)}`} className="inline-flex items-center gap-1 hover:text-[#C85A32]"><Mail size={11} /> {d.reporter_email}</a>
                  {d.reporter_phone && <a href={`tel:+91${d.reporter_phone}`} className="inline-flex items-center gap-1 hover:text-[#C85A32]"><Phone size={11} /> {d.reporter_phone}</a>}
                  {d.amount_involved_inr ? <span>Amount involved: {inr(d.amount_involved_inr)}</span> : null}
                </div>
                <p className="mt-2 text-sm whitespace-pre-wrap">{d.description}</p>
                <div className="mt-2 text-xs" data-testid={`dispute-payout-${d.id}`}>
                  {d.payout_status_now ? (
                    <span className={`px-2 py-0.5 rounded ${d.payout_status_now === "on_hold" ? "bg-[#1A1F2C] text-white" : d.payout_status_now === "paid" ? "bg-[#D4DEC6] text-[#2B3A67]" : "bg-[#C85A32]/15 text-[#C85A32]"}`}>
                      Payout {inr(d.booking_amount_paise / 100)} · {d.payout_status_now.replace("_", " ")}{d.payout_hold_reason ? ` — ${d.payout_hold_reason}` : ""}
                    </span>
                  ) : <span className="text-[#5C6B7A]">No LocaNest payment attached (free-launch deal)</span>}
                </div>
                {d.resolution_note && <div className="mt-2 text-xs text-[#2B3A67]">Resolution ({d.outcome?.replace(/_/g, " ")}): {d.resolution_note}</div>}
              </div>
              <div className="flex md:flex-col gap-2 justify-end">
                {d.status === "open" ? (
                  <>
                    {d.payout_status_now && d.payout_status_now !== "on_hold" && d.payout_status_now !== "paid" && (
                      <Button size="sm" onClick={() => holdPayout(d)} className="bg-[#1A1F2C] hover:bg-black text-white rounded-md" data-testid={`dispute-hold-${d.id}`}><PauseCircle size={12} className="mr-1" /> Hold payout</Button>
                    )}
                    <Button size="sm" onClick={() => setSel(d)} className="bg-[#2B3A67] hover:bg-[#1f2c52] text-white rounded-md" data-testid={`dispute-resolve-${d.id}`}><CheckCircle2 size={12} className="mr-1" /> Resolve</Button>
                  </>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => reopen(d.id)} className="rounded-md" data-testid={`dispute-reopen-${d.id}`}><RotateCcw size={12} className="mr-1" /> Reopen</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent data-testid="dispute-dialog">
          <DialogHeader><DialogTitle>Resolve report #{sel?.id?.slice(0, 8)}</DialogTitle><DialogDescription>The reporter will be emailed the outcome. Release or refund the payout separately from the Payouts section.</DialogDescription></DialogHeader>
          <Select value={outcome} onValueChange={setOutcome}><SelectTrigger data-testid="dispute-outcome"><SelectValue /></SelectTrigger><SelectContent>{OUTCOMES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What was found / what happens next (sent to the reporter)" data-testid="dispute-note" />
          <DialogFooter><Button onClick={resolve} disabled={busy} className="bg-[#2B3A67] hover:bg-[#1f2c52] text-white" data-testid="dispute-resolve-confirm">{busy ? "Saving…" : "Mark resolved"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
